import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EmbeddedViewRef,
  NgZone,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';

/**
 * Подсказка, всплывающая по наведению и следующая за курсором.
 *
 * Содержимое задаёт вызывающая сторона через `template` — компонент отвечает только за
 * появление, позиционирование и оформление панели:
 *
 * ```html
 * <mon-hover-popover [template]="tpl" [context]="{ $implicit: row }">
 *   <lib-svg-icon icon="info" [size]="16" />
 * </mon-hover-popover>
 * ```
 *
 * Панель переносится в `body`, а не остаётся на месте компонента. Иначе `position: fixed`
 * отсчитывался бы не от вьюпорта: карточка устройства объявляет `will-change: transform`
 * и `backdrop-filter`, а любое из этих свойств делает элемент containing block для
 * фиксированных потомков, и подсказка уезжала бы вместе с карточкой.
 *
 * Из-за переноса оформление панели живёт в глобальных стилях (`styles/global/templates.scss`):
 * у созданного вручную элемента нет атрибута инкапсуляции, и scoped-стили к нему не применяются.
 */
@Component({
  selector: 'mon-hover-popover',
  templateUrl: './hover-popover.component.html',
  styleUrl: './hover-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HoverPopoverComponent implements OnDestroy {
  public readonly template = input.required<TemplateRef<unknown>>();
  public readonly context = input<Record<string, unknown> | null>(null);

  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);

  private panelEl: HTMLElement | null = null;
  private viewRef: EmbeddedViewRef<unknown> | null = null;
  private boundMouseMove: ((event: MouseEvent) => void) | null = null;

  private readonly OFFSET_X = 16;
  private readonly OFFSET_Y = 16;
  private readonly VIEWPORT_MARGIN = 8;

  public ngOnDestroy(): void {
    this.close();
  }

  protected onEnter(event: MouseEvent): void {
    this.close();
    this.open();
    this.updatePosition(event.clientX, event.clientY);

    // Слежение за курсором вне зоны Angular: иначе каждое движение мыши запускало бы
    // проверку изменений во всём приложении.
    this.zone.runOutsideAngular(() => {
      this.boundMouseMove = (moveEvent: MouseEvent): void => {
        this.updatePosition(moveEvent.clientX, moveEvent.clientY);
      };
      this.document.addEventListener('mousemove', this.boundMouseMove);
    });
  }

  protected onLeave(): void {
    this.close();
  }

  private open(): void {
    this.viewRef = this.viewContainerRef.createEmbeddedView(
      this.template(),
      this.context() ?? undefined,
    );
    this.viewRef.detectChanges();

    this.panelEl = this.document.createElement('div');
    this.panelEl.className = 'hover-popover-panel';
    this.panelEl.setAttribute('role', 'tooltip');

    for (const node of this.viewRef.rootNodes) {
      this.panelEl.appendChild(node as Node);
    }

    this.document.body.appendChild(this.panelEl);
  }

  private close(): void {
    if (this.boundMouseMove) {
      this.document.removeEventListener('mousemove', this.boundMouseMove);
      this.boundMouseMove = null;
    }

    this.panelEl?.remove();
    this.panelEl = null;

    this.viewRef?.destroy();
    this.viewRef = null;
  }

  private updatePosition(clientX: number, clientY: number): void {
    if (!this.panelEl) {
      return;
    }

    const width = this.panelEl.offsetWidth;
    const height = this.panelEl.offsetHeight;

    const maxX = Math.max(this.VIEWPORT_MARGIN, window.innerWidth - width - this.VIEWPORT_MARGIN);
    const maxY = Math.max(this.VIEWPORT_MARGIN, window.innerHeight - height - this.VIEWPORT_MARGIN);

    const x = Math.min(Math.max(this.VIEWPORT_MARGIN, clientX + this.OFFSET_X), maxX);
    const y = Math.min(Math.max(this.VIEWPORT_MARGIN, clientY + this.OFFSET_Y), maxY);

    this.panelEl.style.left = `${x}px`;
    this.panelEl.style.top = `${y}px`;
  }
}
